import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["timeline", "button"]
  static values = {
    successMessage: { type: String, default: "Copied!" },
    buttonText: { type: String, default: "Copy" }
  }

  copy() {
    const markdown = this.buildMarkdown()

    navigator.clipboard.writeText(markdown).then(() => {
      this.showSuccess()
    }).catch(() => {
      this.showError()
    })
  }

  buildMarkdown() {
    const timeline = this.timelineTarget
    const items = timeline.querySelectorAll(".group.relative")
    const lines = ["# Customer Journey\n"]

    items.forEach(item => {
      const badge = item.querySelector(".inline-flex.items-center.px-1\\.5")
      const time = item.querySelector(".text-xs.text-gray-400")?.textContent?.trim()
      const link = item.querySelector("a[href]")
      const referrer = item.querySelector(".text-gray-500.truncate, .text-neutral-500.truncate")
      const sender = item.querySelector(".font-semibold.text-sm")
      const messageContent = item.querySelector(".prose")

      if (badge && link) {
        // Page view item
        const pageType = badge.textContent.trim()
        const title = link.textContent.trim()
        const url = link.getAttribute("href")

        lines.push(`## ${pageType} - ${time || ""}`)
        lines.push(`**[${title}](${url})**`)
        if (referrer) {
          lines.push(`_${referrer.textContent.trim()}_`)
        }
        lines.push("")
      } else if (sender && messageContent) {
        // Message item
        const role = sender.textContent.trim()
        const content = this.extractTextContent(messageContent)

        lines.push(`## ${role} - ${time || ""}`)
        lines.push(content)
        lines.push("")
      }
    })

    return lines.join("\n")
  }

  extractTextContent(element) {
    // Clone to avoid modifying the original
    const clone = element.cloneNode(true)

    // Convert links to markdown
    clone.querySelectorAll("a").forEach(link => {
      const text = link.textContent
      const href = link.getAttribute("href")
      link.replaceWith(`[${text}](${href})`)
    })

    // Convert bold to markdown
    clone.querySelectorAll("strong, b").forEach(bold => {
      bold.replaceWith(`**${bold.textContent}**`)
    })

    // Convert italic to markdown
    clone.querySelectorAll("em, i").forEach(italic => {
      italic.replaceWith(`_${italic.textContent}_`)
    })

    // Get text content and clean up
    return clone.textContent
      .split("\n")
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .join("\n")
  }

  showSuccess() {
    if (!this.hasButtonTarget) return

    const originalContent = this.buttonTarget.innerHTML
    this.buttonTarget.innerHTML = `
      <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
      ${this.successMessageValue}
    `
    this.buttonTarget.classList.remove("text-gray-500", "hover:text-gray-700", "dark:text-neutral-400", "dark:hover:text-neutral-200")
    this.buttonTarget.classList.add("text-green-600", "dark:text-green-400")

    setTimeout(() => {
      this.buttonTarget.innerHTML = originalContent
      this.buttonTarget.classList.add("text-gray-500", "hover:text-gray-700", "dark:text-neutral-400", "dark:hover:text-neutral-200")
      this.buttonTarget.classList.remove("text-green-600", "dark:text-green-400")
    }, 2000)
  }

  showError() {
    if (!this.hasButtonTarget) return

    const originalContent = this.buttonTarget.innerHTML
    this.buttonTarget.innerHTML = `
      <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" x2="9" y1="9" y2="15"></line><line x1="9" x2="15" y1="9" y2="15"></line></svg>
      Failed
    `
    this.buttonTarget.classList.remove("text-gray-500", "hover:text-gray-700", "dark:text-neutral-400", "dark:hover:text-neutral-200")
    this.buttonTarget.classList.add("text-red-600", "dark:text-red-400")

    setTimeout(() => {
      this.buttonTarget.innerHTML = originalContent
      this.buttonTarget.classList.add("text-gray-500", "hover:text-gray-700", "dark:text-neutral-400", "dark:hover:text-neutral-200")
      this.buttonTarget.classList.remove("text-red-600", "dark:text-red-400")
    }, 2000)
  }
}
